import { createStyles, Flex } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Confetti from 'react-confetti';

import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Button } from '~/domains/design-system/Button';
import { SuccessCheckmark } from '~/domains/design-system/icons/SuccessCheckmark';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import BalanceUpdate from '~/domains/design-system/components/BalanceUpdate/BalanceUpdate';
import { IS_STORYBOOK } from '~/config/const';
import dt from '~/testing';
import { useAutoPickStrategy } from '~/domains/contest/domains/common/hooks/useAutoPickStrategy';
import { PromoCard } from '~/domains/design-system/components/PromoCard/PromoCard';
import type { PromoCardProps } from '~/domains/promotions/types/carousel.types';
import {
  hasDualBalanceProps,
  hasSingleBalanceProps,
} from '~/domains/payments/utils/balanceUtils';

import { REDIRECT_TIMEOUT } from '../useEnterContestModal';

import { useStyles } from './EnterContestSuccessScreenModal.styles';


function EnterContestSuccessScreen({
  entryCount,
  isOpen,
  onClose,
  redirectTo,
  balanceInDollars,
  previousBalanceInDollars,
  usedCredits = false,
  hasAutoPicksEnabled = false,
  contestId,
  onEditAutoPicks,
  // New props for combined balance display
  cashBalanceInDollars,
  previousCashBalanceInDollars,
  creditsBalanceInDollars,
  previousCreditsBalanceInDollars,
  promoCardProps,
}: {
  entryCount: number;
  isOpen: boolean;
  onClose?: () => void;
  redirectTo: string;
  // Single balance display (when user paid with only cash or only credits)
  balanceInDollars?: number;
  previousBalanceInDollars?: number;
  usedCredits?: boolean;
  hasAutoPicksEnabled?: boolean;
  contestId?: string;
  onEditAutoPicks?: () => void;
  // Dual balance display (when both cash AND credits changed - combined balance entry)
  cashBalanceInDollars?: number;
  previousCashBalanceInDollars?: number;
  creditsBalanceInDollars?: number;
  previousCreditsBalanceInDollars?: number;
  promoCardProps?: PromoCardProps;
}) {
  const { t } = useTranslation('contest');
  const [remaining, setRemaining] = useState(REDIRECT_TIMEOUT);
  const timerRef = useRef<NodeJS.Timeout>();
  const { classes, theme } = useStyles();
  const router = useRouter();

  const { data: autoPickStrategyData } = useAutoPickStrategy(
    contestId,
    hasAutoPicksEnabled && !!contestId
  );

  useEffect(() => {
    // Don't auto-redirect if auto-picks are enabled
    if (hasAutoPicksEnabled) {
      return;
    }

    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        const nextValue = prev - 1000;
        if (nextValue > 0) {
          return nextValue;
        }

        clearInterval(timerRef.current);
        if (IS_STORYBOOK) {
          return 0;
        }

        router.push(redirectTo);
        onClose?.();

        return 0;
      });
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
    };
  }, [hasAutoPicksEnabled, redirectTo, onClose, router]);

  const getAutoPicksMessage = () => {
    if (!hasAutoPicksEnabled) return null;

    if (autoPickStrategyData?.strategy && autoPickStrategyData?.availableStrategies) {
      const currentStrategyLabel = autoPickStrategyData.availableStrategies.find(
        (strategy) => strategy.value === autoPickStrategyData.strategy
      )?.label;

      if (currentStrategyLabel) {
        return t('common.enterContestModal.success.autoPicksMessageWithStrategy', {
          strategy: currentStrategyLabel,
        });
      }
    }

    return t('common.enterContestModal.success.autoPicksMessage');
  };

  // Determine which balance display to render (dual or single)
  const dualBalanceProps = {
    cashBalance: cashBalanceInDollars,
    previousCashBalance: previousCashBalanceInDollars,
    creditsBalance: creditsBalanceInDollars,
    previousCreditsBalance: previousCreditsBalanceInDollars,
  };

  const singleBalanceProps = {
    balance: balanceInDollars,
    previousBalance: previousBalanceInDollars,
  };

  const showDualBalance = hasDualBalanceProps(dualBalanceProps);
  const showSingleBalance = !showDualBalance && hasSingleBalanceProps(singleBalanceProps);

  return (
    <>
      {isOpen && <Confetti recycle={false} />}
      <Dialog type={{ desktop: 'modal', mobile: 'modal-full-screen' }} isOpen={isOpen}>
        <DialogBody
          className={classes.container}
          data-test-id={dt.contest.common.enterContestModal.successScreen.container}
        >
          <div className={classes.header}>
            <SuccessCheckmark checkmarkColor={theme.colors.blue[3]} />
            <div className={classes.content}>
              <Title
                tag="h1"
                variant="headline-xxl"
                c="gray2.6"
                data-test-id={dt.contest.common.enterContestModal.successScreen.title}
              >
                {t('common.enterContestModal.success.title')}
              </Title>
              <Text tag="p" variant="title-medium" c="gray2.5" ta="center" m={0}>
                {t('common.enterContestModal.success.textPrefix')}{' '}
                <Text tag="span" variant="headline-medium" c="gray2.5">
                  {t('common.enterContestModal.success.entryCount__hasPlural', { count: entryCount })}
                </Text>{' '}
                {t('common.enterContestModal.success.textSuffix')}
              </Text>
            </div>
          </div>

          {promoCardProps && (
            <div className={classes.promoCardContainer}>
              <PromoCard
                {...promoCardProps}
                variant="default"
                isSelected={false}
                showBorder
                isApplied
              />
            </div>
          )}

          {/* Dual balance display when both cash and credits changed */}
          {showDualBalance && (
            <div className={classes.balanceContainer}>
              <BalanceUpdate
                previousBalance={dualBalanceProps.previousCashBalance}
                newBalance={dualBalanceProps.cashBalance}
                balanceType="cash"
                showBorder={false}
                showBottomDivider
              />
              <BalanceUpdate
                previousBalance={dualBalanceProps.previousCreditsBalance}
                newBalance={dualBalanceProps.creditsBalance}
                balanceType="credits"
                showBorder={false}
              />
            </div>
          )}

          {/* Single balance display when only cash or credits changed */}
          {showSingleBalance && (
            <Flex w="100%" maw={300}>
              <BalanceUpdate
                previousBalance={singleBalanceProps.previousBalance}
                newBalance={singleBalanceProps.balance}
                balanceType={usedCredits ? 'credits' : 'cash'}
              />
            </Flex>
          )}

          {hasAutoPicksEnabled ? (
            <Flex direction="column" align="center" gap={theme.spacing.lg} mt={theme.spacing.lg}>
              <Text variant="body-large" color={theme.colors.gray2[3]} maw={400} ta="center">
                {getAutoPicksMessage()}
              </Text>

              <Flex direction="column" gap={theme.spacing.sm} style={{ width: '100%' }}>
                <Button
                  variant="primary-fill"
                  size="medium"
                  fullWidth
                  onClick={() => {
                    onClose?.();
                    router.push(redirectTo);
                  }}
                >
                  {t('common.enterContestModal.success.gotIt')}
                </Button>

                <Button variant="text" size="medium" fullWidth onClick={onEditAutoPicks}>
                  {t('common.enterContestModal.success.editAutoPickLogic')}
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Text variant="title-small" color={theme.colors.gray2[3]} mt={theme.other.spacing._16}>
              {t('common.enterContestModal.success.redirecting', {
                seconds: remaining / 1000,
              })}
            </Text>
          )}
        </DialogBody>
      </Dialog>
    </>
  );
}

export default EnterContestSuccessScreen;
